import React from "react";
import { Badge, Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { Info } from "lucide-react";

interface RestrictionTypeDisplayProps {
  /** true = Restrictive, false = Permissive, undefined = no event selected */
  restrictionType?: boolean;
  /** Translation function */
  t: (key: string) => string;
}

/**
 * Renders a rich tooltip label with bold title and description
 */
const RichTooltipLabel: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <Box>
    <Text fontWeight="bold" fontSize="12px" mb="4px">
      {title}
    </Text>
    <Text fontWeight="normal" fontSize="12px">
      {description}
    </Text>
  </Box>
);

/**
 * RestrictionTypeDisplay - Read-only display of the event's restriction type.
 * Shows a compact label + info icon with a tag/badge below.
 * Matches the Figma design as a column aligned with the selectors.
 */
export const RestrictionTypeDisplay: React.FC<RestrictionTypeDisplayProps> = ({
  restrictionType,
  t,
}) => {
  const hasValue = restrictionType !== undefined;
  const isRestrictive = restrictionType === true;

  return (
    <Flex direction="column" gap="12px" align="center" justify="center">
      <Flex align="center" gap="8px">
        <Text
          fontSize="14px"
          fontWeight="500"
          color="#242E38"
          lineHeight="24px"
          whiteSpace="nowrap"
        >
          {t("eventAction")}
        </Text>
        <Tooltip
          label={
            <RichTooltipLabel
              title={t("eventAction")}
              description={t("eventActionTooltip")}
            />
          }
          placement="top"
          hasArrow
          bg="#242E38"
          color="white"
          borderRadius="8px"
          p="10px"
          maxW="240px"
        >
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            cursor="pointer"
            lineHeight="0"
          >
            <Info size={16} color="#7e7b7b" style={{ marginBottom: "2px" }} />
          </Box>
        </Tooltip>
      </Flex>
      {hasValue ? (
        <Badge
          bg="#DBF1FE"
          color="#0884CC"
          fontSize="14px"
          fontWeight="400"
          px="8px"
          py="2px"
          h="24px"
          minH="16px"
          borderRadius="4px"
          textTransform="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {isRestrictive ? t("restrictive") : t("permissive")}
        </Badge>
      ) : (
        <Text fontSize="14px" color="#717680" lineHeight="24px">
          -
        </Text>
      )}
    </Flex>
  );
};
